import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Save, Rocket, Play, Loader2, AlertCircle, CheckCircle2, Download, Plus, Trash2 } from 'lucide-react';

import RuleConditionsEditor from './RuleConditionsEditor';
import RuleStagesEditor from './RuleStagesEditor';
import { buildDesignFromSegments, blankRule } from './bpmApprovalFlowShared';
import {
  saveApprovalDraft,
  updateApprovalDraft,
  deployApprovalDraft,
  simulateApprovalDraft,
  formatDraftApiError,
} from './approvalDraftApi';

function cloneDesign(d) {
  return d ? JSON.parse(JSON.stringify(d)) : null;
}

function pushCategory(cat) {
  const c = String(cat || '').toLowerCase();
  if (c.includes('time') || c.includes('labor')) return 'time_labor';
  return 'absence';
}

function download(filename, text) {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Slide-over editor: edit IF (conditions) + THEN (approver stages) for a tenant task,
 * save as a draft, and deploy to the tenant (live REST when available, else a
 * downloadable deploy package). Reuses the proven draft + push pipeline.
 */
export default function BpmRuleAuthoringDrawer({ open, onClose, api, task, meta, segments, seedDesign = null }) {
  const category = pushCategory(task?.category);
  const [design, setDesign] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mongoId, setMongoId] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [deployResult, setDeployResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [attributes, setAttributes] = useState([]);

  // Load the registered transaction attributes for this category so the IF
  // condition picker shows the full set (not a hardcoded short list).
  useEffect(() => {
    if (!open || !api) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/copilot/bpm/attributes', {
          params: { category },
          validateStatus: () => true,
        });
        if (!cancelled && res.status === 200) {
          setAttributes(Array.isArray(res.data?.attributes) ? res.data.attributes : []);
        }
      } catch (_) {
        /* keep fallback list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, api, category]);

  // Seed the design when the drawer opens. A provided seedDesign (e.g. "copy rule")
  // wins; otherwise seed from the task's configured segments.
  const seedKey = seedDesign?._copiedFrom || '';
  useEffect(() => {
    if (!open) return;
    setDesign(seedDesign ? cloneDesign(seedDesign) : buildDesignFromSegments(segments, meta, task, category));
    setActiveIdx(0);
    setMongoId(null);
    setError('');
    setNotice('');
    setDeployResult(null);
    setDryRun(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.taskKey, task?.code, seedKey]);

  const ruleName = useMemo(
    () => task?.name || design?.transactionHint || 'Approval rule',
    [task?.name, design?.transactionHint]
  );

  const rules = design?.rules || [];
  const safeIdx = Math.min(activeIdx, Math.max(0, rules.length - 1));
  const activeRule = rules[safeIdx] || null;

  // Update the active rule, keeping top-level conditions/stages mirrored to it so
  // simulate/compare/validate (single-rule consumers) operate on the active rule.
  const updateActiveRule = useCallback(
    (patch) => {
      setDesign((d) => {
        if (!d) return d;
        const list = d.rules || [];
        const idx = Math.min(activeIdx, Math.max(0, list.length - 1));
        const nextRules = list.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        const cur = nextRules[idx] || {};
        return {
          ...d,
          rules: nextRules,
          conditions: cur.conditions || d.conditions,
          stages: cur.stages || d.stages,
        };
      });
    },
    [activeIdx]
  );

  const onConditionsChange = useCallback(
    (conditions) => updateActiveRule({ conditions }),
    [updateActiveRule]
  );

  // Stages editor edits the active rule's THEN stages.
  const onStagesChange = useCallback(
    (stages) => updateActiveRule({ stages }),
    [updateActiveRule]
  );

  const addRule = useCallback(() => {
    setDesign((d) => {
      if (!d) return d;
      const list = d.rules || [];
      const next = [...list, blankRule(list.length)];
      setActiveIdx(next.length - 1);
      return { ...d, rules: next };
    });
  }, []);

  const removeRule = useCallback(
    (idx) => {
      setDesign((d) => {
        if (!d) return d;
        const list = d.rules || [];
        if (list.length <= 1) return d;
        const next = list.filter((_, i) => i !== idx);
        const cur = next[0] || {};
        return {
          ...d,
          rules: next,
          conditions: cur.conditions || d.conditions,
          stages: cur.stages || d.stages,
        };
      });
      setActiveIdx((prev) => (prev >= idx && prev > 0 ? prev - 1 : prev));
    },
    []
  );

  const renameRule = useCallback((idx, name) => {
    setDesign((d) => {
      if (!d) return d;
      const list = d.rules || [];
      return { ...d, rules: list.map((r, i) => (i === idx ? { ...r, name } : r)) };
    });
  }, []);

  const ensureSaved = useCallback(async () => {
    const payload = {
      ruleName,
      transaction: design?.transactionHint || ruleName,
      transactionCategory: category,
      design,
      summary: design?.summary || ruleName,
    };
    if (mongoId) {
      const saved = await updateApprovalDraft(api, mongoId, payload);
      return saved?.mongoId || mongoId;
    }
    const saved = await saveApprovalDraft(api, payload);
    if (saved?.mongoId) setMongoId(saved.mongoId);
    return saved?.mongoId || null;
  }, [api, design, ruleName, category, mongoId]);

  const handleSave = useCallback(async () => {
    if (!api || !design) return;
    setBusy('save');
    setError('');
    setNotice('');
    try {
      const id = await ensureSaved();
      setNotice(id ? 'Draft saved.' : 'Saved.');
    } catch (err) {
      setError(formatDraftApiError(err, 'Failed to save draft'));
    } finally {
      setBusy('');
    }
  }, [api, design, ensureSaved]);

  const handleSimulate = useCallback(async () => {
    if (!api || !design) return;
    setBusy('simulate');
    setError('');
    setNotice('');
    try {
      const id = await ensureSaved();
      if (!id) throw new Error('Save the draft before simulating.');
      const result = await simulateApprovalDraft(api, id, { category });
      const path = (result?.path || result?.stages || [])
        .map((s) => s.label || s.approver || s.kind)
        .filter(Boolean)
        .join(' \u2192 ');
      setNotice(path ? `Simulated path: ${path}` : 'Simulation complete.');
    } catch (err) {
      setError(formatDraftApiError(err, 'Simulation failed'));
    } finally {
      setBusy('');
    }
  }, [api, design, ensureSaved, category]);

  const handleDeploy = useCallback(async () => {
    if (!api || !design) return;
    setBusy('deploy');
    setError('');
    setNotice('');
    setDeployResult(null);
    try {
      const id = await ensureSaved();
      if (!id) throw new Error('Save the draft before deploying.');
      const result = await deployApprovalDraft(api, id, { category, dryRun });
      setDeployResult(result?.deploy || result);
      if (result?.ok) {
        const mode = result.deploy?.mode || 'unknown';
        setNotice(
          dryRun
            ? `Dry run complete (mode: ${mode}). Review below, then disable dry run to apply.`
            : `Deploy finished (mode: ${mode}).`
        );
      } else {
        setError(result?.deploy?.summary || 'Deploy did not complete.');
      }
    } catch (err) {
      setError(formatDraftApiError(err, 'Deploy failed'));
    } finally {
      setBusy('');
    }
  }, [api, design, ensureSaved, category, dryRun]);

  if (!open) return null;

  const restPush = deployResult?.mode === 'rest_push';
  const manualExport = deployResult && deployResult.mode !== 'rest_push';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-slate-800">Edit IF/THEN &amp; Deploy</h2>
            <p className="truncate text-xs text-slate-500">
              {ruleName}
              {task?.code ? ` \u00b7 ${task.code}` : ''}
              {` \u00b7 ${category}`}
              {` \u00b7 ${rules.length} rule${rules.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Seeded from the configured task. Edit each rule's IF conditions and THEN approvers, add
            or remove rules, then deploy the whole ruleset. Live push applies only if this pod
            exposes a writable rule endpoint; otherwise you get a downloadable deploy package.
          </div>

          {/* Rule tabs (the ruleset) */}
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {rules.map((r, i) => (
              <div
                key={i}
                className={`group inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs ${
                  i === safeIdx
                    ? 'border-cyan-500 bg-cyan-50 text-cyan-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <button type="button" onClick={() => setActiveIdx(i)} className="font-medium">
                  {r.name || `Rule ${i + 1}`}
                </button>
                {rules.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeRule(i)}
                    title="Remove rule"
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              onClick={addRule}
              disabled={busy !== ''}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:border-cyan-400 hover:text-cyan-700 disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" /> Add rule
            </button>
          </div>

          {/* Active rule name */}
          {activeRule ? (
            <label className="mb-3 block text-xs text-slate-600">
              Rule name
              <input
                type="text"
                value={activeRule.name || ''}
                onChange={(e) => renameRule(safeIdx, e.target.value)}
                disabled={busy !== ''}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
          ) : null}

          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {'IF \u2014 conditions'}
          </h3>
          <RuleConditionsEditor
            conditions={activeRule?.conditions || []}
            onChange={onConditionsChange}
            disabled={busy !== ''}
            attributes={attributes}
          />

          <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {'THEN \u2014 approver routing'}
          </h3>
          <RuleStagesEditor
            stages={activeRule?.stages || []}
            onChange={onStagesChange}
            disabled={busy !== ''}
          />

          {/* Deploy result */}
          {deployResult ? (
            <div className="mt-4 rounded-lg border border-slate-200 p-3 text-xs">
              <div className="mb-1 flex items-center gap-2 font-semibold text-slate-700">
                {restPush ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                )}
                Deploy result: {deployResult.mode}
              </div>
              <p className="text-slate-600">{deployResult.summary}</p>
              {Array.isArray(deployResult.hints) && deployResult.hints.length ? (
                <ul className="mt-2 list-disc pl-5 text-slate-500">
                  {deployResult.hints.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              ) : null}
              {manualExport ? (
                <button
                  type="button"
                  onClick={() =>
                    download(
                      `approval-deploy-${(task?.code || 'rule').toLowerCase()}.json`,
                      JSON.stringify(deployResult, null, 2)
                    )
                  }
                  className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3.5 w-3.5" /> Download deploy package
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-200 px-5 py-3">
          {error ? (
            <div className="mb-2 flex items-center gap-1.5 text-xs text-rose-600">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          ) : null}
          {notice ? <div className="mb-2 text-xs text-emerald-700">{notice}</div> : null}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={busy !== ''}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save draft
            </button>
            <button
              type="button"
              onClick={handleSimulate}
              disabled={busy !== ''}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === 'simulate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Simulate
            </button>
            <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                disabled={busy !== ''}
              />
              Dry run
            </label>
            <button
              type="button"
              onClick={handleDeploy}
              disabled={busy !== ''}
              className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {busy === 'deploy' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
              {dryRun ? 'Deploy (dry run)' : 'Deploy to tenant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
